import {
  DERIVED_CERTIFICATION_TARGETS,
  calculateDerivedMetrics,
  calculateProvenanceMargin,
  certificationDecision,
  type DerivedMetrics,
} from "./interpolantDerivedMetrics";

export type ProvenanceMode = "interpolated" | "extrapolated";

type CoefficientKey = "cl" | "cd" | "cm";

export type DimensionKey =
  | "alphaDeg"
  | "betaDeg"
  | "mach"
  | "rpmLeft"
  | "rpmRight"
  | "flapDeg"
  | "elevatorDeg"
  | "cgPercentMac"
  | "altitudeFt"
  | "weightLb"
  | "aileronDeg"
  | "nacelleYawDeg";

export type DesignVector = Record<DimensionKey, number>;

export interface DesignDimension {
  key: DimensionKey;
  label: string;
  shortLabel: string;
  unit: string;
  min: number;
  max: number;
  nominal: number;
}

export interface PresetQuery {
  id: string;
  label: string;
  intent: string;
  input: DesignVector;
}

export interface BasisProjection {
  id: string;
  label: string;
  value: number;
}

export interface AircraftSupportCase {
  id: string;
  operationId: string;
  input: DesignVector;
  latent: [number, number, number];
  coefficients: Record<CoefficientKey, number>;
}

export interface SupportCase {
  id: string;
  operationId: string;
  distance: number;
  weight: number;
}

export interface DimensionStatus {
  key: DimensionKey;
  label: string;
  value: number;
  min: number;
  max: number;
  mode: ProvenanceMode;
}

export interface ArtifactNode {
  id: string;
  conditionLabel: string;
  dataPointId: string;
  label: string;
  kind: "surrogate" | "derived" | "decision";
  formula: string;
  quantityLabel: string;
  value: string;
  mode: ProvenanceMode;
  confidence: number;
  parentIds: string[];
  supportIds: string[];
}

export interface EvaluationResult {
  input: DesignVector;
  mode: ProvenanceMode;
  reasons: string[];
  supportIds: string[];
  supportCases: SupportCase[];
  dimensionStatuses: DimensionStatus[];
  latent: BasisProjection[];
  nearestSupportDistance: number;
  provenanceMargin: number;
  coefficients: Record<CoefficientKey, number>;
  derivedMetrics: DerivedMetrics;
  artifacts: ArtifactNode[];
}

const FIT_OPERATION_ID = "op-tailor-aircraft-surrogate-fit";
const BASIS_OPERATION_ID = "op-tailor-aircraft-basis";
const TRUST_RADIUS = 1.18;
const SUPPORT_COUNT = 6;
const IN_DOMAIN_REASON = "inside 12D aircraft flight envelope and support radius";
const CERTIFICATION_POINT_ID = "aDB-CERT-042";
const CERTIFICATION_POINT_LABEL = "APP-042 approach certification point";

export const DESIGN_DIMENSIONS: DesignDimension[] = [
  { key: "alphaDeg", label: "Angle of attack", shortLabel: "alpha", unit: "deg", min: -2, max: 14, nominal: 5.5 },
  { key: "betaDeg", label: "Sideslip", shortLabel: "beta", unit: "deg", min: -6, max: 6, nominal: 0 },
  { key: "mach", label: "Mach", shortLabel: "Mach", unit: "", min: 0.12, max: 0.28, nominal: 0.18 },
  { key: "rpmLeft", label: "Left propulsor", shortLabel: "L rpm", unit: "rpm", min: 2400, max: 4800, nominal: 3600 },
  { key: "rpmRight", label: "Right propulsor", shortLabel: "R rpm", unit: "rpm", min: 2400, max: 4800, nominal: 3600 },
  { key: "flapDeg", label: "Flap", shortLabel: "flap", unit: "deg", min: 0, max: 20, nominal: 8 },
  { key: "elevatorDeg", label: "Elevator", shortLabel: "elev", unit: "deg", min: -8, max: 8, nominal: 0 },
  { key: "cgPercentMac", label: "CG", shortLabel: "CG", unit: "% MAC", min: 22, max: 35, nominal: 28 },
  { key: "altitudeFt", label: "Altitude", shortLabel: "alt", unit: "ft", min: 0, max: 8000, nominal: 2500 },
  { key: "weightLb", label: "Weight", shortLabel: "weight", unit: "lb", min: 2600, max: 3600, nominal: 3050 },
  { key: "aileronDeg", label: "Aileron", shortLabel: "ail", unit: "deg", min: -5, max: 5, nominal: 0 },
  { key: "nacelleYawDeg", label: "Nacelle yaw", shortLabel: "yaw", unit: "deg", min: -3, max: 3, nominal: 0 },
];

type BasisTerm = { key: DimensionKey; weight: number };

export const TAILOR_BASIS: Array<{ id: string; label: string; terms: BasisTerm[] }> = [
  {
    id: "lift-loading",
    label: "Lift/loading mode",
    terms: [
      { key: "alphaDeg", weight: 0.46 },
      { key: "flapDeg", weight: 0.33 },
      { key: "rpmLeft", weight: 0.2 },
      { key: "rpmRight", weight: 0.2 },
      { key: "mach", weight: -0.15 },
      { key: "weightLb", weight: -0.13 },
    ],
  },
  {
    id: "trim-stability",
    label: "Trim/stability mode",
    terms: [
      { key: "elevatorDeg", weight: -0.43 },
      { key: "cgPercentMac", weight: 0.38 },
      { key: "alphaDeg", weight: -0.22 },
      { key: "mach", weight: 0.17 },
      { key: "altitudeFt", weight: 0.13 },
    ],
  },
  {
    id: "asymmetry-control",
    label: "Asymmetry/control mode",
    terms: [
      { key: "rpmRight", weight: 0.35 },
      { key: "rpmLeft", weight: -0.35 },
      { key: "betaDeg", weight: 0.36 },
      { key: "aileronDeg", weight: -0.28 },
      { key: "nacelleYawDeg", weight: 0.24 },
    ],
  },
];

const DIMENSION_BY_KEY = new Map(DESIGN_DIMENSIONS.map((dimension) => [dimension.key, dimension]));

export const NOMINAL_VECTOR = vectorFrom(
  Object.fromEntries(DESIGN_DIMENSIONS.map((dimension) => [dimension.key, dimension.nominal])),
);

export const INTERPOLANT_DOMAIN = {
  dims: DESIGN_DIMENSIONS.length,
  sampledRuns: 64,
  supportCount: SUPPORT_COUNT,
  trustRadius: TRUST_RADIUS,
  fitOperationId: FIT_OPERATION_ID,
  method: "Tailor reduced-basis aircraft response surface with sparse Flow360 support cases",
};

function vectorFrom(values: Partial<Record<DimensionKey, number>>): DesignVector {
  const result: Partial<DesignVector> = {};
  for (const dimension of DESIGN_DIMENSIONS) {
    result[dimension.key] = values[dimension.key] ?? dimension.nominal;
  }
  return result as DesignVector;
}

function withValues(values: Partial<Record<DimensionKey, number>>): DesignVector {
  return vectorFrom({ ...NOMINAL_VECTOR, ...values });
}

export const PRESET_QUERIES: PresetQuery[] = [
  {
    id: "cruise",
    label: "Cruise",
    intent: "Nominal powered-lift aircraft condition inside the 12D sampled envelope.",
    input: withValues({ alphaDeg: 4.8, mach: 0.18, rpmLeft: 3550, rpmRight: 3600, flapDeg: 6, altitudeFt: 2400 }),
  },
  {
    id: "coupled-edge",
    label: "Coupled edge",
    intent: "A high-dimensional control, CG, and propulsor corner that is still inside validated support.",
    input: withValues({
      alphaDeg: 11.8,
      betaDeg: 3.6,
      mach: 0.24,
      rpmLeft: 4300,
      rpmRight: 4550,
      flapDeg: 16,
      elevatorDeg: -3.5,
      cgPercentMac: 31.5,
      altitudeFt: 6100,
      weightLb: 3330,
      aileronDeg: -1.8,
      nacelleYawDeg: 1.2,
    }),
  },
  {
    id: "control-boundary",
    label: "Control boundary",
    intent: "Control-margin check on the sampled alpha boundary; still interpolated, but with zero envelope slack.",
    input: withValues({
      alphaDeg: 14,
      mach: 0.2,
      rpmLeft: 3720,
      rpmRight: 3780,
      flapDeg: 12,
      elevatorDeg: -2,
      cgPercentMac: 30,
      altitudeFt: 3200,
      weightLb: 3140,
    }),
  },
  {
    id: "alpha-outside",
    label: "Alpha outside",
    intent: "Above the sampled aircraft alpha envelope.",
    input: withValues({ alphaDeg: 17.2, rpmLeft: 4000, rpmRight: 4080, flapDeg: 13, elevatorDeg: -2.5 }),
  },
  {
    id: "rpm-outside",
    label: "RPM outside",
    intent: "Both propulsors exceed the sampled powered-lift envelope.",
    input: withValues({ alphaDeg: 8.4, rpmLeft: 5350, rpmRight: 5280, betaDeg: 1.5, flapDeg: 12, aileronDeg: -1 }),
  },
];

function dimensionFor(key: DimensionKey): DesignDimension {
  const dimension = DIMENSION_BY_KEY.get(key);
  if (!dimension) throw new Error(`Unknown dimension ${key}`);
  return dimension;
}

function normalize(key: DimensionKey, value: number) {
  const dimension = dimensionFor(key);
  const mid = (dimension.min + dimension.max) / 2;
  const halfRange = (dimension.max - dimension.min) / 2;
  return (value - mid) / halfRange;
}

function denormalize(key: DimensionKey, scaled: number) {
  const dimension = dimensionFor(key);
  const mid = (dimension.min + dimension.max) / 2;
  const halfRange = (dimension.max - dimension.min) / 2;
  return mid + scaled * halfRange;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function clamp01(value: number) {
  return clamp(value, 0, 1);
}

function projectionValues(input: DesignVector): [number, number, number] {
  return TAILOR_BASIS.map((axis) =>
    axis.terms.reduce((sum, term) => sum + normalize(term.key, input[term.key]) * term.weight, 0),
  ) as [number, number, number];
}

function basisProjection(input: DesignVector): BasisProjection[] {
  const values = projectionValues(input);
  return TAILOR_BASIS.map((axis, index) => ({
    id: axis.id,
    label: axis.label,
    value: values[index]!,
  }));
}

function deterministicOffset(index: number, dimensionIndex: number) {
  const primary = Math.sin((index + 1) * (dimensionIndex + 2) * 1.37);
  const secondary = Math.cos((index + 3) * (dimensionIndex + 1) * 0.71);
  return clamp(0.72 * primary + 0.18 * secondary, -0.92, 0.92);
}

function buildSupportCase(index: number): AircraftSupportCase {
  const input = vectorFrom(
    Object.fromEntries(
      DESIGN_DIMENSIONS.map((dimension, dimensionIndex) => [
        dimension.key,
        denormalize(dimension.key, deterministicOffset(index, dimensionIndex)),
      ]),
    ),
  );
  return {
    id: `cfd-case-${String(index + 1).padStart(3, "0")}`,
    operationId: `op-flow360-aircraft-case-${String(index + 1).padStart(3, "0")}`,
    input,
    latent: projectionValues(input),
    coefficients: sourceCoefficient(input),
  };
}

function sourceCoefficient(input: DesignVector): Record<CoefficientKey, number> {
  const n = Object.fromEntries(DESIGN_DIMENSIONS.map((dimension) => [dimension.key, normalize(dimension.key, input[dimension.key])])) as Record<
    DimensionKey,
    number
  >;
  const rpmMean = (n.rpmLeft + n.rpmRight) / 2;
  const rpmSplit = (n.rpmRight - n.rpmLeft) / 2;

  return {
    cl:
      0.82 +
      0.34 * n.alphaDeg +
      0.13 * n.flapDeg +
      0.1 * rpmMean -
      0.05 * n.mach -
      0.06 * n.betaDeg ** 2 -
      0.04 * n.weightLb +
      0.025 * n.elevatorDeg -
      0.035 * n.altitudeFt +
      0.018 * n.alphaDeg * n.flapDeg,
    cd:
      0.058 +
      0.029 * (n.alphaDeg + 0.12) ** 2 +
      0.011 * n.betaDeg ** 2 +
      0.014 * n.flapDeg ** 2 +
      0.014 * rpmMean ** 2 +
      0.007 * rpmSplit ** 2 +
      0.007 * n.mach +
      0.006 * n.weightLb,
    cm:
      -0.045 -
      0.1 * n.alphaDeg -
      0.069 * n.elevatorDeg +
      0.075 * n.cgPercentMac +
      0.017 * n.flapDeg -
      0.018 * rpmMean +
      0.011 * n.betaDeg * n.nacelleYawDeg -
      0.011 * n.aileronDeg * n.betaDeg,
  };
}

export const AIRCRAFT_SUPPORT_CASES: AircraftSupportCase[] = Array.from({ length: INTERPOLANT_DOMAIN.sampledRuns }, (_, index) =>
  buildSupportCase(index),
);

function distance3(a: [number, number, number], b: [number, number, number]) {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

function supportFor(input: DesignVector): SupportCase[] {
  const latent = projectionValues(input);
  const nearest = AIRCRAFT_SUPPORT_CASES.map((supportCase) => ({
    supportCase,
    distance: distance3(latent, supportCase.latent),
  }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, SUPPORT_COUNT);
  const rawWeights = nearest.map(({ distance }) => Math.exp(-((distance / 0.78) ** 2)));
  const weightSum = rawWeights.reduce((sum, weight) => sum + weight, 0) || 1;

  return nearest.map(({ supportCase, distance }, index) => ({
    id: supportCase.id,
    operationId: supportCase.operationId,
    distance,
    weight: rawWeights[index]! / weightSum,
  }));
}

function predictCoefficients(support: SupportCase[]): Record<CoefficientKey, number> {
  return support.reduce(
    (acc, supportItem) => {
      const supportCase = AIRCRAFT_SUPPORT_CASES.find((candidate) => candidate.id === supportItem.id);
      if (!supportCase) return acc;
      acc.cl += supportCase.coefficients.cl * supportItem.weight;
      acc.cd += supportCase.coefficients.cd * supportItem.weight;
      acc.cm += supportCase.coefficients.cm * supportItem.weight;
      return acc;
    },
    { cl: 0, cd: 0, cm: 0 },
  );
}

function dimensionStatuses(input: DesignVector): DimensionStatus[] {
  return DESIGN_DIMENSIONS.map((dimension) => {
    const value = input[dimension.key];
    return {
      key: dimension.key,
      label: dimension.shortLabel,
      value,
      min: dimension.min,
      max: dimension.max,
      mode: value < dimension.min || value > dimension.max ? "extrapolated" : "interpolated",
    };
  });
}

function domainReasons(statuses: DimensionStatus[], nearestDistance: number) {
  const reasons: string[] = [];
  for (const status of statuses) {
    if (status.value < status.min) {
      reasons.push(`${status.label} below sampled min ${formatNumber(status.min)}`);
    }
    if (status.value > status.max) {
      reasons.push(`${status.label} above sampled max ${formatNumber(status.max)}`);
    }
  }
  if (nearestDistance > TRUST_RADIUS) {
    reasons.push(`outside aircraft support radius (${nearestDistance.toFixed(2)} > ${TRUST_RADIUS.toFixed(2)})`);
  }
  if (reasons.length === 0) {
    reasons.push(IN_DOMAIN_REASON);
  }
  return reasons;
}

function confidence(nearestDistance: number, statuses: DimensionStatus[]) {
  const largestViolation = Math.max(
    0,
    ...statuses.map((status) => {
      const range = status.max - status.min;
      if (status.value < status.min) return (status.min - status.value) / range;
      if (status.value > status.max) return (status.value - status.max) / range;
      return 0;
    }),
  );
  return clamp01(0.96 - 0.38 * (nearestDistance / TRUST_RADIUS) - 0.95 * largestViolation);
}

function formatNumber(value: number, digits = Math.abs(value) >= 100 ? 0 : 2) {
  const formatted = value.toFixed(digits);
  return formatted.includes(".") ? formatted.replace(/\.?0+$/, "") : formatted;
}

function numeric(value: number, digits = 3) {
  return value.toFixed(digits);
}

export function evaluateInterpolantChain(input: DesignVector): EvaluationResult {
  const supportCases = supportFor(input);
  const supportIds = supportCases.map((support) => support.id);
  const coefficients = predictCoefficients(supportCases);
  const statuses = dimensionStatuses(input);
  const nearestSupportDistance = supportCases[0]?.distance ?? Number.POSITIVE_INFINITY;
  const reasons = domainReasons(statuses, nearestSupportDistance);
  const mode: ProvenanceMode = reasons[0] === IN_DOMAIN_REASON ? "interpolated" : "extrapolated";
  const conf = confidence(nearestSupportDistance, statuses);
  const provenanceMargin = calculateProvenanceMargin(nearestSupportDistance, TRUST_RADIUS, statuses);
  const derivedMetrics = calculateDerivedMetrics(coefficients);

  const decision = certificationDecision(mode, derivedMetrics.certificationMargin);

  const artifacts: ArtifactNode[] = [
    {
      id: "aircraft-surrogate",
      conditionLabel: CERTIFICATION_POINT_LABEL,
      dataPointId: `${CERTIFICATION_POINT_ID}.CL`,
      label: `${CERTIFICATION_POINT_ID} · CL`,
      kind: "surrogate",
      formula: "CL = surrogate(12D flight condition)",
      quantityLabel: "Lift coefficient",
      value: numeric(coefficients.cl),
      mode,
      confidence: conf,
      parentIds: [FIT_OPERATION_ID, BASIS_OPERATION_ID],
      supportIds,
    },
    {
      id: "ld",
      conditionLabel: CERTIFICATION_POINT_LABEL,
      dataPointId: `${CERTIFICATION_POINT_ID}.LD`,
      label: `${CERTIFICATION_POINT_ID} · L/D`,
      kind: "derived",
      formula: "L/D = CL / CD",
      quantityLabel: "Lift-to-drag ratio",
      value: numeric(derivedMetrics.ld, 2),
      mode,
      confidence: conf,
      parentIds: ["aircraft-surrogate"],
      supportIds,
    },
    {
      id: "lift-reserve",
      conditionLabel: CERTIFICATION_POINT_LABEL,
      dataPointId: `${CERTIFICATION_POINT_ID}.CL_RES`,
      label: `${CERTIFICATION_POINT_ID} · CL reserve`,
      kind: "derived",
      formula: `CL - required_CL (${DERIVED_CERTIFICATION_TARGETS.requiredCl})`,
      quantityLabel: "Lift reserve",
      value: numeric(derivedMetrics.liftReserve, 3),
      mode,
      confidence: conf,
      parentIds: ["aircraft-surrogate"],
      supportIds,
    },
    {
      id: "pitch-margin",
      conditionLabel: CERTIFICATION_POINT_LABEL,
      dataPointId: `${CERTIFICATION_POINT_ID}.CM_MARGIN`,
      label: `${CERTIFICATION_POINT_ID} · control margin`,
      kind: "derived",
      formula: "-CM - 0.035",
      quantityLabel: "Control margin",
      value: numeric(derivedMetrics.pitchMargin, 3),
      mode,
      confidence: conf,
      parentIds: ["aircraft-surrogate"],
      supportIds,
    },
    {
      id: "cert-margin",
      conditionLabel: CERTIFICATION_POINT_LABEL,
      dataPointId: `${CERTIFICATION_POINT_ID}.REQ_MARGIN`,
      label: `${CERTIFICATION_POINT_ID} · requirement margin`,
      kind: "decision",
      formula: "weighted margins to L/D, lift, and pitch requirements",
      quantityLabel: "Requirement margin",
      value: `${numeric(derivedMetrics.certificationMargin, 1)} pts - ${decision}`,
      mode,
      confidence: conf,
      parentIds: ["ld", "lift-reserve", "pitch-margin"],
      supportIds,
    },
  ];

  return {
    input,
    mode,
    reasons,
    supportIds,
    supportCases,
    dimensionStatuses: statuses,
    latent: basisProjection(input),
    nearestSupportDistance,
    provenanceMargin,
    coefficients,
    derivedMetrics,
    artifacts,
  };
}
